import React from 'react';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import DataExtractorViewer from 'components/DataExtractorViewer';
import PageIntro from 'components/PageIntro';
import { Button, Typography } from 'design-component';
import { palette } from 'styles/color';
import FileButton from 'components/FileButton';
import { Trans, useTranslation } from 'react-i18next';
import ga, { EventOpenSample, EventUpload } from 'util/gaConnect';
import { getSampleArchiveType, SAMPLE_FILE_DATA } from 'data/sample';
import { IconUpload } from 'assets/icon';
import { useAppSelector } from 'store/store';
import { userInfoSelector } from 'store/slices/userInfo';
import { languageSelector } from 'store/slices/languageSlice';
import { StlyedLink } from 'pages/Api/Usage';
import { unzip } from 'unzipit';

type DocumentTypes = 'hwp' | 'word' | 'slide' | 'sheet' | 'pdf';
export type SampleType = DocumentTypes | 'upload';
export type JsonType = 'unified' | 'full';

const DocExtractor = () => {
  // const userInfo = useAppSelector(userInfoSelector);
  const { t } = useTranslation();
  const { isLogged } = useAppSelector(userInfoSelector);
  const lang = useAppSelector(languageSelector);
  const [file, setFile] = useState<File>();
  const [selectedType, setSelectedType] = useState<SampleType>(lang === 'ko' ? 'hwp' : 'word');
  const [sampleData, setSampleData] = useState<any>(null);
  // const [sampleType, setSampleType] = useState<(typeof SampleTypes)[number] | null>(null);
  const [schemaType, setSchemaType] = useState<JsonType>('unified');

  const clearSampleData = () => {
    setSampleData(null);
    setFile(undefined);
    setSelectedType('upload');
  };

  const handleSelectType = (type: SampleType) => {
    if (type === selectedType) return;
    setSchemaType('unified');
    setFile(undefined);
    setSampleData(type === 'upload' ? null : SAMPLE_FILE_DATA(lang)[type]);
    setSelectedType(type);
  };

  const onOpenSample = async (type: SampleType, signal: AbortSignal) => {
    if (type === 'upload') {
      setSampleData(null);
    } else {
      const staticSampleData = SAMPLE_FILE_DATA(lang)[type];
      setSampleData(staticSampleData);
      ga.event(EventOpenSample(type));

      try {
        const archiveType = getSampleArchiveType(type);
        const resource = await fetch(`/sampleZip/${archiveType}-sample-${lang}.zip`, { signal });
        if (!resource.ok) {
          throw new Error(`Failed to fetch sample archive: ${resource.status}`);
        }

        const outerArchive = await unzip(await resource.blob());
        const dataEntry = outerArchive.entries['data.zip'];
        if (!dataEntry) {
          throw new Error('data.zip not found in sample archive');
        }

        const resultArchive = await dataEntry.blob();

        if (signal.aborted) return;
        setSampleData({
          ...staticSampleData,
          resultArchive
        });
      } catch (e) {
        if (signal.aborted) return;
        console.error('Error in handleSelectType:', e);
      }
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    void onOpenSample(selectedType, controller.signal);
    return () => controller.abort();
  }, [selectedType, lang]);

  useEffect(() => {
    if (!isLogged) {
      setFile(undefined);
    }
    setSelectedType(lang === 'ko' ? 'hwp' : 'word');
  }, [isLogged, lang]);

  const getDocumentTypes = ({
    lang,
    _types
  }: {
    lang: 'ko' | 'en';
    _types: DocumentTypes[];
  }): DocumentTypes[] => {
    return lang === 'ko' ? _types : ['word', 'slide', 'sheet', 'pdf', 'hwp'];
  };

  return (
    <PlayGroundBase>
      <PageIntro title={t('docExtract')}>
        <Typography size="16px" weight={'normal'} color={palette.gray[500]}>
          {t('docExtractDesc')}
        </Typography>
        <Typography
          size="16px"
          weight={'normal'}
          color={palette.gray[500]}
          style={{
            fontStyle: 'italic',
            marginTop: '-8px'
          }}>
          <Trans
            i18nKey="betaDesc"
            components={{
              1: (
                <StlyedLink
                  to={
                    lang === 'ko'
                      ? 'mailto:sales@polarisoffice.com '
                      : 'mailto:globalsales@polarisoffice.com'
                  }
                />
              )
            }}
          />
        </Typography>
      </PageIntro>
      <Typography
        size="18px"
        weight={500}
        color={palette.gray[800]}
        style={{ margin: '28px 0px 12px 0px' }}>
        {t('selectSampleFile')}
      </Typography>
      <Toolbar>
        <>
          {getDocumentTypes({ lang, _types: ['hwp', 'word', 'slide', 'sheet', 'pdf'] }).map(
            (type) => (
              <FileButton
                key={type}
                type={type}
                onClick={() => handleSelectType(type)}
                style={{
                  color: selectedType === type ? palette.blue[500] : palette.gray[800],
                  border: `1px solid ${
                    selectedType === type ? palette.blue[500] : palette.gray[200]
                  }`,
                  background: selectedType === type ? palette.white : 'initial'
                }}></FileButton>
            )
          )}
        </>
        <Button
          disabled={schemaType === 'full'}
          variant="outlined"
          color="primary"
          startIcon={<IconUpload />}
          onClick={() => handleSelectType('upload')}
          // 활성 정보 prop

          style={{
            color: selectedType === 'upload' ? palette.blue[500] : palette.gray[800],
            border: `1px solid ${
              selectedType === 'upload' ? palette.blue[500] : palette.gray[200]
            }`,
            background: selectedType === 'upload' ? palette.white : 'initial'
          }}>
          {t('upload')}
        </Button>
        {/* <UploadButton
          onClick={(e) => {
            if (!userInfo.isLogged) {
              e.preventDefault();
              e.stopPropagation();
              apiWrapper(`/api/v1/datainsight/playground/userinfo`);
            }
            ga.event(EventUpload);
          }}
          onChange={(file) => setFile(file)}></UploadButton> */}
      </Toolbar>
      <DataExtractorViewer
        file={file}
        setFile={setFile}
        sampleData={sampleData}
        selectedType={selectedType}
        setSelectedType={setSelectedType}
        clearSampleData={clearSampleData}
        schemaType={schemaType}
        setSchemaType={setSchemaType}></DataExtractorViewer>
    </PlayGroundBase>
  );
};

export default DocExtractor;

const PlayGroundBase = styled.div`
  width: 100%;
  // height: 100%;
  display: flex;
  flex-direction: column;
`;
const Toolbar = styled.div`
  width: 100%;
  height: 40px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;

  margin-bottom: 19px;
`;
